/**
 * Structured form-submission envelope.
 *
 * `render_ui` is not a `clientSide` tool — its call resolves immediately — so a
 * form's values cannot come back as a tool result. They travel instead as a new
 * user turn carrying a fenced `form-submission` block:
 *
 * ```form-submission
 * { "formId": "pickup_request", "values": { "mobile": "+974…" } }
 * ```
 *
 * The model receives keyed values (not the prose that `${/path}` interpolation
 * produced before); the transcript renders a compact chip instead of raw JSON.
 *
 * @module lib/json-render/form-submission
 */

/** Parsed contents of a form-submission envelope. */
export interface FormSubmissionEnvelope {
  formId: string;
  summary?: string;
  values: Record<string, unknown>;
}

/** Matches the fenced envelope, tolerating surrounding whitespace. */
const ENVELOPE_RE = /^\s*```form-submission\s*\n([\s\S]*?)\n?```\s*$/;

/**
 * Parses a user message body as a form-submission envelope.
 *
 * @param text - Raw message text
 * @returns The parsed envelope, or null when this isn't one (the common case)
 */
export function parseFormSubmission(
  text: string | undefined | null
): FormSubmissionEnvelope | null {
  if (!text || !text.includes("```form-submission")) return null;
  const match = ENVELOPE_RE.exec(text);
  if (!match) return null;
  try {
    const parsed = JSON.parse(match[1]) as FormSubmissionEnvelope;
    if (!parsed || typeof parsed !== "object" || !parsed.values) return null;
    return parsed;
  } catch {
    // Malformed payload — fall back to rendering the raw text.
    return null;
  }
}

/** An ISO calendar date on its own, e.g. `2026-08-02`. */
const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A time range written with a plain hyphen, e.g. `08:00-12:00`. */
const TIME_RANGE_RE = /^(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})$/;

/**
 * Presents a submitted value the way a person reads it.
 *
 * Only two rewrites, both unambiguous on their own: an ISO date becomes
 * day/month/year, and a hyphenated time range gets an arrow so the two ends
 * don't read as a subtraction. Anything else is left exactly as submitted — the
 * value still has to match what was stored.
 *
 * @param value - One raw submitted value
 * @returns The display string
 */
function formatValue(value: unknown): string {
  if (Array.isArray(value)) return value.join(", ");
  if (value === true) return "Yes";

  const text = String(value);

  const date = ISO_DATE_RE.exec(text);
  if (date) return `${date[3]}/${date[2]}/${date[1]}`;

  const range = TIME_RANGE_RE.exec(text);
  // Wrapped in an LTR isolate (U+2066…U+2069): inside an Arabic message the
  // paragraph direction would otherwise reorder the two times around the arrow,
  // so "12:00 → 15:00" renders as "15:00 → 12:00". The isolate pins the run
  // left-to-right and is invisible in both directions.
  if (range) return `⁦${range[1]} → ${range[2]}⁩`;

  return text;
}

/**
 * Human-readable field list for the transcript chip, skipping empties.
 *
 * @param values - The submitted values object
 * @returns `key: value` pairs ready to render
 */
export function summarizeSubmission(
  values: Record<string, unknown>
): Array<{ key: string; value: string }> {
  return Object.entries(values)
    .filter(
      ([, v]) =>
        v !== undefined &&
        v !== null &&
        v !== false &&
        !(typeof v === "string" && v.trim().length === 0) &&
        !(Array.isArray(v) && v.length === 0)
    )
    .map(([key, v]) => ({
      key: key.replace(/[_-]+/g, " "),
      value: formatValue(v),
    }));
}
